import { IUserLinkedAccount, ILink, IUserDevice, SubscriptionCategory, SubscriptionType, FamilyLinkType, IUser } from '@bciti/types';
import { IPermissionScope } from '../auth/auth.model';

import { OrganisationConfiguration, Organisation } from '../global/global.model';

export interface PendingRequestCounts {
    pendingCardRequests: number;
    pendingFamilyLinkRequests: number;
    pendingResidences: number;
}

export interface UserState {
    profile?: Profile;
    userRecord?: UserRecord;
    userRecordEmailChangedState?: secEmailState;
    userSearch?: {
        total: number;
        pageIndex: number;
        itemsPerPage: number;
        quickSearchForm?: QuickSearchForm;
        advancedSearchForm?: AdvancedSearchForm;
        activeForm?: AvailableUserSearchForms;
        users: UserRecord[];
        quickFilters?: {
            cardsToApproveActive: boolean;
            familyLinksToApproveActive: boolean;
            residencesToApproveActive: boolean;
            moreFiltersData: Record<string, any>;
        };
    };
    scopes?: IPermissionScope[];
}

export interface secEmailState {
    initialEmail?: string;
    targetEmail?: string;
    changePending?: boolean;
    expires?: number;
}

export enum AvailableUserSearchForms {
    quickSearch = 'QuickSearch',
    advancedSearch = 'AdvancedSearch',
}

export interface QuickSearchForm {
    search: string;
}

export interface AdvancedSearchForm {
    firstName: string;
    lastName: string;
    email: string;
    birthDate: string;
    phone: string;
    role: string;
    confirmed: boolean;
    number: string;
    streetName: string;
    postCode: string;
    cardStatus: string;
    cardCode: string;
    familyStatus: string;
    residenceStatus: string;
    pendingCardApproval: string;
}

export enum Sex {
    male = 'male',
    female = 'female',
    other = 'other',
}

export enum Roles {
    admin = 'admin',
    user = 'user',
}

export interface Address {
    postCode: string;
    number: number;
    street: string;
    city: string;
    province: string;
    longitude: number;
    latitude: number;
    streetName?: string;
    type?: string;
    direction?: string;
    unit?: string;
    isPrimary?: boolean;
}

export interface User {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    secEmail: string;
    confirmed: boolean;
    role: string;
    cardNumber: string;
    unregistered?: boolean;
}

export interface UserResponse {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    secEmail: string;
    confirmed: boolean;
    role: string;
    cardNumber: string;
    family: Family;
    birthDate?: string;
    phone?: string;
    picture?: string;
}

export interface Profile extends User {
    picture?: string;
    birthDate?: string;
    phone?: string;
    sex?: Sex;
    preferences?: UserPreferences;
    secPhone?: string;
    secPhoneExt?: string;
    cellPhone?: string;
    addresses?: Address[];
}

export interface UserPreferences {
    language?: string;
    requestFilters?: any;
    permitFilters?: any;
}

export interface ProfileResponse extends UserResponse {
    picture?: string;
    birthDate?: string;
    phone?: string;
    sex?: Sex;
    secPhone?: string;
    secPhoneExt?: string;
    preferences?: UserPreferences;
    cellPhone?: string;
    addresses?: Address[];
    linkedAccounts?: IUserLinkedAccount[];
}

export interface UserRecord extends User {
    picture?: string;
    birthDate?: string;
    phone?: string;
    sex?: Sex;
    secPhone?: string;
    secPhoneExt?: string;
    cellPhone?: string;
    addresses?: Address[];
}

export interface UserRecordResponse extends UserResponse {
    picture?: string;
    birthDate?: string;
    phone?: string;
    sex?: Sex;
    secPhone?: string;
    secPhoneExt?: string;
    cellPhone?: string;
    addresses?: Address[];
    cards: UserCard[];
}

export interface CreateUserRecordPayload {
    firstName: string;
    lastName: string;
    email: string;
    picture?: string;
    birthDate?: string;
    phone?: string;
    sex?: Sex;
    secPhone?: string;
    secPhoneExt?: string;
    cellPhone?: string;
    addresses?: Address[];
    orgId: string;
    clientId?: string;
}

export interface AdminRegisterPayload {
    email: string;
    firstName: string;
    lastName: string;
    orgId: string;
    clientId?: string;
}

export interface UpdateUserRecordPayload {
    firstName?: string;
    lastName?: string;
    email?: string;
    secEmail?: string;
    picture?: string;
    birthDate?: string;
    phone?: string;
    sex?: Sex;
    secPhone?: string;
    secPhoneExt?: string;
    cellPhone?: string;
    addresses?: Address[];
    preferences?: UserPreferences;
}

export interface ManageRolePayload {
    roleId: string;
}

export interface UserCard {
    cardType: string;
    firstName: string;
    lastName: string;
    photo: string;
    _id: string;
    number: string;
    renewal: any;
    expirationDate: string;
    subscriptionDate: string;
    lastModified: string;
    created: string;
    status: string;
    address: Address;
    sex: string;
    birthDate: string;
    actionType?: CardActions;
}

export interface UserRecordTransformedUser {
    id: string;
    access: string;
    name: string;
    email: string;
    secEmail: string;
    cardNumber: string;
    active: string;
    emailConfirmed: boolean;
    birthDate: string;
    phone: string;
    secPhone: string;
    secPhoneExt: string;
    sex: string;
    firstName: string;
    lastName: string;
    bcitiCard: undefined | UserCard;
    sportsCard: undefined | UserCard;
    libraryCard: undefined | UserCard;
    citizenCard: undefined | UserCard;
    legacyCard: undefined | UserCard;
    userFamily: UserFamily;
    cardNeedAction?: boolean;
    preferences?: UserPreferences;
    picture?: string;
}

export interface UserFamily {
    family: Family;
}

export interface Family {
    organisation: string;
    links: FamilyLink[];
}

export interface FamilyLink {
    userId: Partial<Profile>;
    link: string;
    familyApproved: boolean;
    cityApproved: boolean;
    proofOfIdentity: string[];
    conflicts?: string[];
}

export interface PatchApproveRefuseFamilyLink {
    id: string;
    family_member_id: string;
    confirmed: boolean;
    reason?: string;
}

export interface GetUserActiveSubscriptionsResponse {
    cardType: string;
    code: string;
    cost: number;
    description: string;
    isRenewable: boolean;
    endDate: string;
    purposeStartDate: string;
    purposeEndDate: string;
    category: SubscriptionCategory;
    subscriptionType: SubscriptionType;
    icon: string;
    messages: string[];
    name: string;
    startDate: string;
    status: string;
    taxable: boolean;
    _id?: string;
}

export interface UserCards {
    _id: string;
    cardType: CardType;
    number: string;
    code: string;
    address: IAddresses;
    expirationDate: string;
    subcriptionDate: string;
    status: CardStatus;
    lastName: string;
    firstName: string;
    photo: string;
    created: string;
    lastModified: string;
    proofOfIdentity: string[];
    renewal?: UserCards;
    linkable?: boolean;
    subType?: CardSubType;
    constraints?: CardContraints;
    actionType?: CardActionType;
    sex?: Sex;
    birthDate?: Date;
}

export interface PostNewFamilyLink {
    picture?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    sourceLink: FamiliyLink;
    targetLink: FamiliyLink;
    proofOfIdentity?: string[];
    birthDate?: Date;
    address: LinkAddress;
    sex?: string;
}

export interface LinkAddress {
    unit: string;
    direction: string;
    typeStreet: string;
    latitude: number;
    longitude: number;
    isPrimary: boolean;
    name: string;
    posdtCode: string;
    number: string;
    street: string;
    city: string;
    province: string;
    streetName: string;
}

export interface GetFamilyLinksItem extends Omit<ILink, 'userId' | 'proofOfIdentity'> {
    userId: {
        addresses?: IAddresses[];
        birthDate?: string;
        externalIds?: IUserExternalIdList;
        firstName?: string;
        lastName?: string;
        picture?: string;
        secEmail?: string;
        email?: string;
        sex?: string;
        unregistered?: boolean;
        _id?: string;
    };
    proofOfIdentity?: string[];
    _id: string;
}

export interface IUserExternalIdList {
    users: string[];
}

export interface IAddresses {
    postCode: string;
    number: number;
    street: string;
    city: string;
    province: string;
    latitude: number;
    longitude: number;
    streetName: string;
    type: string;
    direction: string;
    unit: string;
}

export interface SubscribeCardPayload {
    cardType: string;
    address: UserCardAddress;
    lastName: string;
    firstName: string;
    birthDate: string;
    sex: string;
    photo: string;
    proofOfIdentity: string[];
}

export interface UserCardAddress {
    city: string;
    direction: any;
    latitude: number;
    longitude: number;
    number: string;
    postCode: string;
    province: string;
    street: string;
    streetName: string;
    unit: string;
}

export interface UserSearchQuery {
    firstName?: string;
    lastName?: string;
    email?: string;
    sex?: string;
    cardCode?: string;
    birthDate?: Date;
}

export interface CardProfile extends Omit<IUser, 'roles' | 'organisations' | 'files' | 'wallets' | 'otherPhone' | 'device' | 'birthDate' | 'family'> {
    _id: string;
    id: string;
    birthDate: string | Date;
    family: UserFamilyLink[];
    otherPhone?: string;
    device?: IUserDevice;
}

export interface UserWallet {
    // TODO Review this interface when wallet/user data structure is refactored
    _id?: string;
    organisation?: string;
    user?: string;
    cards?: UserCardFE[];
}

export interface PostUserFamilyLinkPayload {
    sourceLink: FamilyLinkType;
    targetLink: FamilyLinkType;
    proofOfIdentity: string[];
}

export interface SearchAddressPayload {
    streetAddress: string;
    postalCode: string;
    city: string;
    unit: string;
}

export interface UserCardFE extends UserCards {
    renewalAlertDisplay?: boolean;
    subscriptions?: GetUserActiveSubscriptionsResponse[];
    linkable?: boolean;
}

export interface CardSelectorFE {
    label: string;
    cardType: CardType;
    status: CardStatus;
    renewal?: any;
}

export interface CardContraints {
    displayInfo: boolean;
    message: string;
}

export interface LinkCardPayload {
    cardType: string;
    cardNumber: string;
}

export interface RenewCardPayload extends SubscribeCardPayload {
    _id: string;
    userId: string;
}

export interface CardActionPayload {
    action: CardActions;
    card: Partial<UserCardFE>;
}

export interface CardConfigDataFE {
    org: Organisation;
    config: OrganisationConfiguration;
}

export interface UserFamilyLink {
    organisation: string;
    links: Link[];
}

export interface WalletDictionnary {
    [key: string]: Partial<UserCardFE>;
}

export interface FEWallet {
    [key: string]: Partial<UserCardFE>;
}

export interface UserCardDiscrepanciesResponse {
    message: string;
    users: { firstName: string; lastName: string; cardNumber: string }[];
}

export interface UserSubscription {
    _id: string;
    order: number;
    icon: string;
    code: string;
    name: string;
    description: string;
    cost: number;
    taxable: boolean;
    cardType: string;
    status: UserOrgSubscriptionStatus;
    messages: UserOrgSubscriptionMessage[];
    isRenewable?: boolean;
    startDate?: string;
    endDate?: string;
    purposeStartDate?: string;
    purposeEndDate?: string;
    subscriptionType: SubscriptionType;
    category: SubscriptionCategory;
}

export interface FESubscription {
    _id: string;
    order: number;
    icon: string;
    code: string;
    inheritanceCode?: string;
    name: string;
    description: string;
    cardType: string;
    cardLabel?: string;
    cardIcon?: string;
    taxable: boolean;
    cost: number;
    durationMonth?: number;
    renewalDelay?: number;
    isRenewable?: boolean;
    startDate?: string;
    endDate?: string;
    purposeStartDate?: string;
    purposeEndDate?: string;
    status: UserOrgSubscriptionStatus;
    messages: UserOrgSubscriptionMessage[];
}

export interface SpreedlyPaymentMethodResponse {
    token: string;
    created_at: string;
    updated_at: string;
    email: string;
    data: any | null;
    storage_state: string;
    test: boolean;
    metadata: {
        key: string;
        another_key: number;
        final_key: boolean;
    };
    callback_url: string | null;
    last_four_digits: string;
    first_six_digits: string;
    card_type: string;
    first_name: string;
    last_name: string;
    month: number;
    year: number;
    address1: string;
    address2: string;
    city: string;
    state: string;
    country: string;
    phone_number: string;
    company: string;
    full_name: string;
    eligible_for_card_updater: boolean;
    shipping_address1: string;
    shipping_address2: string;
    shipping_city: string;
    shipping_state: string;
    shipping_zip: string;
    shipping_country: string;
    shipping_phone_number: string;
    payment_method_type: string;
    errors: string[];
    fingerprint: string;
    verification_value: string;
    number: string;
}

export interface SpreedlyPaymentMethodResponse {
    token: string;
    created_at: string;
    updated_at: string;
    email: string;
    data: any | null;
    storage_state: string;
    test: boolean;
    metadata: {
        key: string;
        another_key: number;
        final_key: boolean;
    };
    callback_url: string | null;
    last_four_digits: string;
    first_six_digits: string;
    card_type: string;
    first_name: string;
    last_name: string;
    month: number;
    year: number;
    address1: string;
    address2: string;
    city: string;
    state: string;
    country: string;
    phone_number: string;
    company: string;
    full_name: string;
    eligible_for_card_updater: boolean;
    shipping_address1: string;
    shipping_address2: string;
    shipping_city: string;
    shipping_state: string;
    shipping_zip: string;
    shipping_country: string;
    shipping_phone_number: string;
    payment_method_type: string;
    errors: string[];
    fingerprint: string;
    verification_value: string;
    number: string;
}

export interface UserOrgSubscriptionMessage {
    categ: UserOrgSubscriptionMessageCategory;
    type: UserOrgSubscriptionMessageType;
    message: string;
}

export type GetUserSubscriptionsResponse = UserSubscription[];

export interface GetUserRequests {
    docs: UserRequest[];
    page: number;
    totalDocs: number;
    limit: number;
}

export interface UserRequest {
    _id: string;
    isPublic: boolean | string;
    requestNum: string;
    external_id: string;
    category: UserRequestCategory;
    subject: UserRequestSubject;
    description: string;
    status: string;
    updated_at: string;
    address?: UserRequestAddress;
    localisation: {
        type: string;
        coordinates: number[];
    };
    user: {
        _id: string;
        firstName: string;
        lastName: string;
    };
    linkedPieces: string[];
}

export interface UserRequestCategory {
    name: string;
    subjects?: string[];
    requests?: string[];
    deleted?: boolean;
    _id: string;
    organisation?: string;
    created_at?: string;
    updated_at?: string;
    __v?: number;
    id: string;
}

export interface UserRequestSubject {
    _id: string;
    name: string;
    id: string;
}

export interface UserRequestAddress extends Address {
    fromGeoposition?: boolean;
}

export enum CardStatus {
    PROCESSING = 'PROCESSING',
    ACTIVE = 'ACTIVE',
    REFUSED = 'REFUSED',
    DELETED = 'DELETED',
    EXPIRED = 'EXPIRED',
    INACTIVE = 'INACTIVE', // front-end only
    ACTIVATING = 'ACTIVATING',
}

export enum CardType {
    BCITI = 'BCITI',
    LEGACY = 'LEGACY',
    SPORTS = 'SPORTS',
    LIBRARY = 'LIBRARY',
    CITIZEN = 'CITIZEN',
}

export enum CardAction {
    SUBSCRIBE = 'SUBSCRIBE',
    RENEW = 'RENEW',
}

export enum FamilyStatus {
    CITYAPPROVAL = 'false',
}

export enum CardActions {
    SUBSCRIBE = 'SUBSCRIBE',
    RENEW = 'RENEW',
    LINK = 'LINK',
    UPDATEEXPDATE = 'UPDATEEXPDATE',
}

export enum CardSubType {
    RESIDENT = 'RESIDENT',
    NONRESIDENT = 'NONRESIDENT',
    MAIN = 'MAIN',
    OTHER = 'OTHER',
}

export enum CardActionType {
    SUBSCRIBE = 'SUBSCRIBE',
    LINK = 'LINK',
}

export enum LinkStatus {
    PENDING = 'PENDING',
    INVITED = 'INVITED',
}

export enum BardCodeType {
    BARCODE = 'barcode',
    QRCODE = 'qrcode',
}

export enum FamiliyLink {
    CHILD = 'CHILD',
    PARENT = 'PARENT',
    OTHER = 'OTHER',
    SPOUSE = 'SPOUSE',
}
export enum PaymentType {
    CREDIT_CARD = 'credit_card',
    IN_PERSON = 'COMPTOIR',
}

export enum UserOrgSubscriptionStatus {
    AVAILABLE = 'AVAILABLE',
    UNAVAILABLE = 'UNAVAILABLE',
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
}

export enum UserOrgSubscriptionMessageCategory {
    CARD_EXP = 'CARD_EXP',
    CARD_TYPE = 'CARD_TYPE',
    MIN_AGE = 'MIN_AGE',
    MAX_AGE = 'MAX_AGE',
    MIN_AGE_BY_DATE = 'MIN_AGE_BY_DATE',
    MAX_AGE_BY_DATE = 'MAX_AGE_BY_DATE',
    RESIDENT = 'RESIDENT',
    NONRESIDENT = 'NONRESIDENT',
    PARENT = 'PARENT',
}

export enum UserOrgSubscriptionMessageType {
    WARNING = 'WARNING',
    ERROR = 'ERROR',
}

export interface UserCardDiscrepanciesResponse {
    message: string;
    users: { firstName: string; lastName: string; cardNumber: string }[];
}

export type CardVerificationType = 'EXACT_MATCH' | 'DUPLICATE_BLOCKED' | 'PARTIAL_MATCH' | 'CARD_EXISTS_INFO_MISMATCH' | 'NO_MATCH';

export interface CardVerificationMatch {
    firstName: string;
    lastName: string;
    number: string;
    code?: string;
    externalId: string;
    birthDate?: string;
    address?: any;
}

export interface CardVerificationResponse {
    verificationType: CardVerificationType;
    matches: CardVerificationMatch[];
    linkedToEmail?: string;
}

export interface UserLink {
    _id: string;
    firstName: string;
    lastName: string;
    picture: string;
    phone?: string;
    secPhone?: string;
    secPhoneExt?: string;
    cellPhone?: string;
    secEmail?: string;
    birthDate?: string;
    sex?: Sex;
    addresses?: Address[];
}

export interface Link {
    _id: string;
    userId: UserLink;
    link: string;
    isSource: boolean;
    familyApproved: boolean;
    cityApproved: boolean;
    deleted: boolean;
}

export interface PostFamilyLink {
    email: string;
    firstName: string;
    lastName: string;
    userId: string;
    reference: FamilyLinkType;
    link: FamilyLinkType;
    proofOfIdentity: string[];
    birthDate: Date;
}

export type PostFamilyLinkPayload = Omit<PostFamilyLink, 'userId' | 'proofOfIdentity'>;

export interface UpdateFamilyLinkPayload {
    targetUserId: string;
    reference: FamilyLinkType;
    link: FamilyLinkType;
}

export interface BcitiPaginatedResult<T> {
    docs: T[];
    hasNextPage?: boolean;
    hasPrevPage?: boolean;
    limit?: number;
    nextPage?: any;
    page?: number;
    pagingCounter?: number;
    prevPage?: any;
    totalDocs?: number;
    totalPages?: number;
}

export interface Vehicule {
    id?: number;
    favorite?: number;
    model: string;
    year?: number;
    plate: string;
    make: string;
    order?: number;
}

// User Activity Types
export enum ActivityType {
    PROFILE_UPDATED = 'PROFILE_UPDATED',
    PROFILE_UPDATED_FROM_ADMIN = 'PROFILE_UPDATED_FROM_ADMIN',
    ACCOUNT_LOGGED = 'ACCOUNT_LOGGED',
    ACCOUNT_CREATED = 'ACCOUNT_CREATED',
    ACCOUNT_CREATED_FROM_ADMIN = 'ACCOUNT_CREATED_FROM_ADMIN',
    ACCOUNT_CONFIRMED = 'ACCOUNT_CONFIRMED',
    ACCOUNT_DELETED = 'ACCOUNT_DELETED',
    PASSWORD_RESET_REQUESTED = 'PASSWORD_RESET_REQUESTED',
    PASSWORD_RESET_REQUESTED_FROM_ADMIN = 'PASSWORD_RESET_REQUESTED_FROM_ADMIN',
    PASSWORD_CHANGED = 'PASSWORD_CHANGED',
    CARD_REQUESTED = 'CARD_REQUESTED',
    CARD_RENEW_REQUESTED = 'CARD_RENEW_REQUESTED',
    CARD_RENEW_APPROVED = 'CARD_RENEW_APPROVED',
    CARD_RENEW_REFUSED = 'CARD_RENEW_REFUSED',
    CARD_RENEWED_FROM_ADMIN = 'CARD_RENEWED_FROM_ADMIN',
    CARD_APPROVED = 'CARD_APPROVED',
    CARD_AUTO_APPROVED = 'CARD_AUTO_APPROVED',
    CARD_REFUSED = 'CARD_REFUSED',
    CARD_LINKED = 'CARD_LINKED',
    CARD_LINKED_FROM_ADMIN = 'CARD_LINKED_FROM_ADMIN',
    CARD_CREATED_FROM_ADMIN = 'CARD_CREATED_FROM_ADMIN',
    CARD_UPDATED = 'CARD_UPDATED',
    CARD_DELETED = 'CARD_DELETED',
    FAMILY_LINK_REQUESTED = 'FAMILY_LINK_REQUESTED',
    FAMILY_LINK_ACCEPTED = 'FAMILY_LINK_ACCEPTED',
    FAMILY_LINK_APPROVED = 'FAMILY_LINK_APPROVED',
    FAMILY_LINK_AUTO_APPROVED = 'FAMILY_LINK_AUTO_APPROVED',
    FAMILY_LINK_REFUSED = 'FAMILY_LINK_REFUSED',
    FAMILY_LINK_CREATED_FROM_ADMIN = 'FAMILY_LINK_CREATED_FROM_ADMIN',
    FAMILY_LINK_DELETED = 'FAMILY_LINK_DELETED',
    FAMILY_LINK_DELETED_FROM_ADMIN = 'FAMILY_LINK_DELETED_FROM_ADMIN',
    FAMILY_LINK_UPDATED = 'FAMILY_LINK_UPDATED',
    ITEM_PURCHASED = 'ITEM_PURCHASED',
    ITEM_ADMIN_PURCHASED = 'ITEM_ADMIN_PURCHASED',
    ITEM_PURCHASED_FAILED = 'ITEM_PURCHASED_FAILED',
    ITEM_RENEWED = 'ITEM_RENEWED',
    ITEM_INHERITED = 'ITEM_INHERITED',
    ITEM_DELETED = 'ITEM_DELETED',
    ITEM_UPDATED = 'ITEM_UPDATED',
    REQUEST_CREATED = 'REQUEST_CREATED',
    CHANNEL_ENABLED = 'CHANNEL_ENABLED',
    CHANNEL_DESABLED = 'CHANNEL_DESABLED',
    CATEGORY_SUBSCRIBED = 'CATEGORY_SUBSCRIBED',
    CATEGORY_UNSUBSCRIBED = 'CATEGORY_UNSUBSCRIBED',
}

export interface UserActivityItem {
    created_at: string;
    metadata: {
        type: ActivityType;
        channelType?: string;
        familyLinkType?: string;
        _id: string;
        admin?: { _id: string; firstName: string; lastName: string };
        familyMember?: { _id: string; firstName: string; lastName: string };
        inheritedFrom?: { _id: string; firstName: string; lastName: string };
        initiator?: { _id: string; firstName: string; lastName: string };
    };
}

export type UserActivityResponse = {
    user: string;
    activities: UserActivityItem[];
};

export type CardSearchStatus = 'LINKED' | 'ALREADY_LINKED' | 'CARD_NOT_YOURS' | 'ALTERNATIVE_FOUND' | 'NOT_FOUND' | 'FOUND';

export interface CardSearchResponse {
    status: CardSearchStatus;
    card?: { number?: string; code?: string; externalId?: string };
    linkedToEmail?: string;
}
